"use client";

import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Quote, ArrowRight, TrendingUp, TrendingDown, MapPin, BookOpen, ChevronLeft, ChevronRight } from "lucide-react";
import { Industry } from "@/types";

interface IndustryCaseStudyProps {
  industry: Industry;
}

const defaultInsights = [
  {
    title: "The Future of Smart Enterprise",
    description: "How AI and IoT are reshaping operations and throughput of modern MSMEs across the country.",
    tag: "Industry Trend",
    imageBg: "bg-gradient-to-br from-[#2C78B9]/10 to-black/10"
  },
  {
    title: "Optimizing the Supply Chain",
    description: "Strategies to handle volatile raw material prices and eliminate logistics bottlenecks.",
    tag: "Strategy",
    imageBg: "bg-gradient-to-br from-[#2C78B9]/10 to-black/20"
  },
  {
    title: "Scaling with Real-time Data",
    description: "Leveraging cloud-based analytics to drive OEE and quality excellence at every stage.",
    tag: "Operational",
    imageBg: "bg-gradient-to-br from-[#2C78B9]/15 to-black/30"
  }
];

export function IndustryCaseStudy({ industry }: IndustryCaseStudyProps) {
  const [activeTab, setActiveTab] = useState("Case Study");
  const { caseStudy } = industry;
  const accentColor = "#2C78B9";

  // Helper to extract initials from author name
  const getInitials = (name: string) => {
    if (!name) return "CS";
    const parts = name.split(" ");
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  };

  return (
    <section className="py-16 sm:py-20 lg:py-24 bg-white overflow-hidden relative">
      {/* Background Ornaments */}
      <div className="absolute top-0 left-0 w-64 h-64 bg-slate-50 rounded-full blur-3xl pointer-events-none -translate-x-1/2 -translate-y-1/2" />
      <div className="absolute bottom-0 right-0 w-80 h-80 bg-slate-50/50 rounded-full blur-3xl pointer-events-none translate-x-1/2 translate-y-1/2" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">

        {/* Section Header */}
        <div className="text-center mb-8 sm:mb-10 lg:mb-12">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 border border-blue-100 mb-5"
          >
            <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ backgroundColor: accentColor }} />
            <span className="text-[10px] font-bold tracking-[0.2em] uppercase" style={{ color: accentColor }}>Client Success</span>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-xl sm:text-2xl md:text-4xl font-[800] text-slate-900 tracking-tight mb-6 sm:mb-8"
          >
            Real Results From <span style={{ color: accentColor }}>{industry.title}</span>
          </motion.h2>

          {/* Tabs */}
          <div className="flex justify-center gap-2 sm:gap-4 mb-8 sm:mb-12 lg:mb-16">
            {["Case Study", "Insights"].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-5 sm:px-8 py-2.5 sm:py-3 rounded-full font-bold text-xs sm:text-sm transition-all duration-300 cursor-pointer ${activeTab === tab
                    ? "text-white shadow-xl"
                    : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                  }`}
                style={{ backgroundColor: activeTab === tab ? accentColor : undefined }}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>

        {/* Content Area */}
        <div className="relative">
          <AnimatePresence mode="wait">
            {activeTab === "Case Study" ? (
              <motion.div
                key="case-study"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="relative"
              >
                {/* Case Study Card */}
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 lg:gap-8 items-start">

                  {/* Left Column: Quote Box */}
                  <div
                    className="lg:col-span-5 rounded-[1.75rem] sm:rounded-[2rem] lg:rounded-[2.5rem] p-6 sm:p-8 md:p-10 text-white flex flex-col justify-between shadow-2xl relative overflow-hidden"
                    style={{ backgroundImage: `linear-gradient(135deg, ${accentColor} 0%, #000000 100%)` }}
                  >
                    <div className="relative z-10">
                      <Quote size={32} className="opacity-25 mb-4 sm:mb-6" />
                      <blockquote className="text-base sm:text-lg md:text-xl lg:text-2xl font-[800] leading-snug sm:leading-tight mb-6 sm:mb-8 tracking-tight italic">
                        "{caseStudy.quote}"
                      </blockquote>
                    </div>

                    <div className="flex items-center gap-3 sm:gap-4 relative z-10 pt-5 sm:pt-6 border-t border-white/10 mt-auto">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white/20 backdrop-blur-md flex items-center justify-center font-black text-sm sm:text-lg shrink-0">
                        {getInitials(caseStudy.author)}
                      </div>
                      <div>
                        <div className="font-black text-sm sm:text-base">{caseStudy.author}</div>
                        <div className="text-[11px] opacity-80 font-bold uppercase tracking-wider">{caseStudy.role}</div>
                        <div className="text-[11px] sm:text-[12px] font-black text-white/90">{caseStudy.company}</div>
                        {caseStudy.location && (
                          <div className="flex items-center gap-1 mt-0.5 text-[9px] sm:text-[10px] text-white/60 font-bold uppercase">
                            <MapPin size={10} />
                            {caseStudy.location}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Decorative Background Glow */}
                    <div className="absolute top-0 right-0 w-56 h-56 bg-white/10 rounded-full blur-[70px] -translate-y-1/2 translate-x-1/2 pointer-events-none" />
                  </div>

                  {/* Right Column: Metrics Grid & Nav */}
                  <div className="lg:col-span-7 flex flex-col gap-4 sm:gap-5">

                    {/* Metrics Grid */}
                    <div className="grid grid-cols-2 gap-3 sm:gap-4">
                      {caseStudy.metrics.map((metric, i) => (
                        <motion.div
                          key={i}
                          initial={{ opacity: 0, y: 16 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: i * 0.08 }}
                          className="bg-slate-50 border border-slate-100 p-4 sm:p-5 md:p-6 lg:p-7 rounded-[1.25rem] sm:rounded-[1.5rem] lg:rounded-[2rem] group hover:bg-white hover:shadow-xl transition-all duration-500 cursor-pointer flex flex-col justify-center min-h-[120px] sm:min-h-[140px]"
                        >
                          <div
                            className="text-xl sm:text-2xl md:text-3xl font-black mb-1.5 flex items-center gap-1.5"
                            style={{ color: metric.trend === "up" ? accentColor : "#64748b" }}
                          >
                            {metric.value}
                            {metric.trend === "up"
                              ? <TrendingUp size={16} className="sm:hidden" />
                              : <TrendingDown size={16} className="sm:hidden" />}
                            {metric.trend === "up"
                              ? <TrendingUp size={20} className="hidden sm:block" />
                              : <TrendingDown size={20} className="hidden sm:block" />}
                          </div>
                          <div className="text-[12px] sm:text-[13px] font-black text-slate-900 mb-0.5 leading-snug">
                            {metric.label}
                          </div>
                          <div className="text-[10px] sm:text-[11px] font-bold text-slate-400 tracking-wide uppercase">
                            Verified Result
                          </div>
                        </motion.div>
                      ))}

                      {/* Placeholder Card if odd metrics count */}
                      {caseStudy.metrics.length % 2 !== 0 && (
                        <div className="bg-slate-50 border border-slate-100 p-4 sm:p-5 rounded-[1.25rem] sm:rounded-[1.5rem] flex flex-col justify-center min-h-[120px] sm:min-h-[140px] opacity-80 border-dashed">
                          <BookOpen size={20} className="text-slate-400 mb-2" />
                          <div className="text-xs sm:text-sm font-bold text-slate-800">Operational Excellence</div>
                          <div className="text-[10px] font-medium text-slate-400 mt-0.5 uppercase">Audit Verified</div>
                        </div>
                      )}
                    </div>

                    {/* Navigation Bar & Action Button */}
                    <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-between items-stretch sm:items-center pt-3 sm:pt-4 border-t border-slate-100">
                      {/* Left: Nav Indicators */}
                      <div className="flex items-center gap-2">
                        <div className="flex gap-2">
                          <button className="w-10 h-10 sm:w-11 sm:h-11 rounded-full border border-slate-200 flex items-center justify-center text-slate-400 cursor-not-allowed">
                            <ChevronLeft size={20} />
                          </button>
                          <button className="w-10 h-10 sm:w-11 sm:h-11 rounded-full border border-slate-200 flex items-center justify-center text-slate-400 cursor-not-allowed">
                            <ChevronRight size={20} />
                          </button>
                        </div>
                        <div className="flex items-center gap-1.5 ml-2">
                          <div
                            className="h-1.5 rounded-full transition-all duration-300"
                            style={{ width: '20px', backgroundColor: accentColor }}
                          />
                        </div>
                      </div>

                      {/* Right: CTA Button
                      <a
                        href="/"
                        className="flex items-center justify-center gap-2 px-6 sm:px-8 py-3 sm:py-4 text-white rounded-full font-bold text-[11px] sm:text-[12px] uppercase tracking-[0.1em] shadow-lg hover:opacity-90 hover:scale-[1.02] transition-all active:scale-95 group cursor-pointer"
                        style={{ backgroundColor: accentColor }}
                      >
                        Our Story
                        <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                      </a> */}
                    </div>
                  </div>
                </div>
              </motion.div>
            ) : (
              <motion.div
                key="insights"
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 30 }}
                className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-5 sm:gap-6 lg:gap-8"
              >
                {defaultInsights.map((insight, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.1 }}
                    className="bg-slate-50 rounded-[1.5rem] sm:rounded-[2rem] lg:rounded-[2.5rem] overflow-hidden border border-slate-100 group cursor-pointer hover:shadow-2xl transition-all duration-500"
                  >
                    {/* Faux Graphic */}
                    <div className={`h-36 sm:h-44 lg:h-48 relative overflow-hidden flex items-center justify-center ${insight.imageBg}`}>
                      <div className="absolute top-3 left-3 z-10 px-2.5 py-1 bg-white/90 backdrop-blur-md rounded-full text-[9px] sm:text-[10px] font-black uppercase tracking-widest" style={{ color: accentColor }}>
                        {insight.tag}
                      </div>
                      <BookOpen size={40} className="text-slate-400/40 group-hover:scale-110 group-hover:rotate-3 transition-transform duration-500" />
                      <div className="absolute inset-0 bg-gradient-to-t from-slate-900/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                    </div>

                    <div className="p-5 sm:p-6 lg:p-8">
                      <h3 className="text-base sm:text-lg lg:text-xl font-black text-slate-900 mb-2.5 sm:mb-3 lg:mb-4 leading-tight group-hover:opacity-80 transition-opacity" style={{ color: accentColor }}>
                        {insight.title}
                      </h3>
                      <p className="text-xs sm:text-sm font-medium text-slate-500 leading-relaxed mb-4 sm:mb-5 lg:mb-6 line-clamp-3">
                        {insight.description}
                      </p>
                      <a
                        href="/contact"
                        className="flex items-center gap-2 font-black text-[10px] sm:text-xs uppercase tracking-widest mt-4"
                        style={{ color: accentColor }}
                      >
                        Read Insights <ArrowRight size={13} className="transform group-hover:translate-x-1 transition-transform" />
                      </a>
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}
